/**
 * @file BillPushStatus.js
 * @version 1.0.2
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-03
 * @modified 2026-10-15
 * @description A bill entered here, on its way to Xero: sent / not yet sent /
 *              trying again / failed with the reason — and Retry. Shown on Add
 *              Bill (after saving, and when editing) and in the bill window.
 *
 * `status` is GET /api/bills/:id/push-status (or a bill row — see
 * utils/billPushStatus). Retry calls POST /api/bills/:id/push-to-xero itself and
 * hands the new status to `onChange`. Its Retry / Send now button is measured
 * here (useSlideOutButton), where it mounts, rather than relying on the host.
 */

import React, { useState } from 'react';
import { API_URL } from '../config';
import { billPushState, mismatchText } from '../utils/billPushStatus';
import { useSlideOutButton } from './SlideOutButton';
import './BillPushStatus.css';

function BillPushStatus({ billId, status, onChange, compact = false }) {
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState(null);
  const state = billPushState(status);
  useSlideOutButton([state?.key, retrying]);
  if (!state) return null;

  const retry = async () => {
    if (!billId) return;
    setRetrying(true);
    setRetryError(null);
    try {
      const res = await fetch(`${API_URL}/api/bills/${billId}/push-to-xero`, { method: 'POST', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (data.push) onChange?.(data.push);
      if (!res.ok && !data.push) setRetryError(data.error || 'Could not send it');
    } catch (e) {
      setRetryError(e.message);
    } finally {
      setRetrying(false);
    }
  };

  const retryTitle = state.canRetry
    ? 'Send this bill to Xero now'
    : state.key === 'switched_off'
      ? 'Sending bills to Xero is switched off — it will go when it is switched on'
      : 'Nothing to send';
  const showRetry = ['queued', 'retrying', 'failed', 'switched_off'].includes(state.key);

  return (
    <div className={`bill-push-status tone-${state.tone}${compact ? ' compact' : ''}`} role="status">
      <span className={`material-symbols-rounded${state.key === 'pushing' || retrying ? ' spinning' : ''}`}>
        {retrying ? 'sync' : state.icon}
      </span>
      <div className="bill-push-status-text">
        <strong>{retrying ? 'Sending to Xero…' : state.label}</strong>
        {state.detail && <span className="bill-push-status-detail">{state.detail}</span>}
        {state.mismatches.length > 0 && (
          <span className="bill-push-status-detail">
            Xero's copy differs — {state.mismatches.map(mismatchText).join('; ')}. Check it in Xero.
          </span>
        )}
        {status?.attempts > 1 && state.key !== 'sent' && (
          <span className="bill-push-status-detail">{status.attempts} attempts so far.</span>
        )}
        {retryError && <span className="bill-push-status-detail">{retryError}</span>}
      </div>
      {status?.xero_url && state.key === 'sent' && (
        <a className="btn-icon" href={status.xero_url} target="_blank" rel="noopener noreferrer" title="Open in Xero">
          <span className="material-symbols-rounded">open_in_new</span>
        </a>
      )}
      {showRetry && (
        <div>
          <button
            type="button"
            className="btn-slideout"
            data-write-action
            onClick={retry}
            disabled={!state.canRetry || retrying}
            title={retryTitle}
          >
            <div className="icon-wrapper">
              <span className={`material-symbols-rounded${retrying ? ' spinning' : ''}`}>{retrying ? 'sync' : 'cloud_upload'}</span>
            </div>
            <span className="btn-label">{state.key === 'failed' || state.key === 'retrying' ? 'Retry' : 'Send now'}</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default BillPushStatus;
